/**
 * PHASE 2 IMPLEMENTATION: Cross-Tab Synchronization Service
 * Implements leader election to ensure only one WebSocket connection per user across tabs
 * Uses BroadcastChannel API for inter-tab communication
 */

import { SocketService } from './socketService';
import { devLog, devWarn } from '../utils/devLog';
import { generateUniqueId } from '../utils/cryptoId';

// Generic type for broadcast data
interface BroadcastData {
  [key: string]: unknown;
}

// Valid message types for security validation
const VALID_MESSAGE_TYPES = [
  'leader-election',
  'leader-heartbeat',
  'socket-event',
  'cache-invalidation',
  'state-sync',
  'socket-room-change'
] as const;

type ValidMessageType = typeof VALID_MESSAGE_TYPES[number];

interface TabMessage {
  type: ValidMessageType;
  tabId: string;
  sourceTabId?: string; // Added to prevent echo events
  timestamp: number;
  data?: BroadcastData;
}

/**
 * SECURITY FIX: Validate incoming BroadcastChannel messages
 * Prevents processing of malformed or potentially malicious messages
 */
function isValidTabMessage(message: unknown): message is TabMessage {
  if (typeof message !== 'object' || message === null) {
    return false;
  }

  const msg = message as Record<string, unknown>;

  // Validate required fields
  if (typeof msg.type !== 'string') {
    return false;
  }

  // Validate message type is one of the expected values
  if (!VALID_MESSAGE_TYPES.includes(msg.type as ValidMessageType)) {
    devWarn(`[CrossTabSync] Invalid message type received: ${msg.type}`);
    return false;
  }

  if (typeof msg.tabId !== 'string' || msg.tabId.length === 0 || msg.tabId.length > 100) {
    return false;
  }

  if (typeof msg.timestamp !== 'number' || !Number.isFinite(msg.timestamp)) {
    return false;
  }

  // Validate timestamp is within reasonable range (not more than 1 minute old or in future)
  const now = Date.now();
  const maxAge = 60000; // 1 minute
  if (msg.timestamp < now - maxAge || msg.timestamp > now + 5000) {
    devWarn(`[CrossTabSync] Message with stale or future timestamp rejected`);
    return false;
  }

  // Optional field validation
  if (msg.sourceTabId !== undefined && (typeof msg.sourceTabId !== 'string' || msg.sourceTabId.length > 100)) {
    return false;
  }

  if (msg.data !== undefined && (typeof msg.data !== 'object' || msg.data === null)) {
    return false;
  }

  return true;
}

export class CrossTabSync {
  private static instance: CrossTabSync;
  private channel: BroadcastChannel | null = null;
  private isLeader = false;
  private tabId: string;
  private leaderId: string | null = null;
  private leaderHeartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private leaderCheckInterval: ReturnType<typeof setInterval> | null = null;
  private socketService: SocketService | null = null;
  private readonly HEARTBEAT_INTERVAL = 2000; // 2 seconds
  private readonly LEADER_TIMEOUT = 5000; // 5 seconds
  private lastLeaderHeartbeat = 0;
  private eventHandlers: Array<{ event: string; handler: EventListener }> = [];
  private messageHandler: ((event: MessageEvent<TabMessage>) => void) | null = null;

  private constructor() {
    // Generate unique tab ID using cryptographically secure randomness
    this.tabId = generateUniqueId('tab');

    // Create broadcast channel for cross-tab communication
    this.channel = new BroadcastChannel('arterra-sync');

    // Set up message listener
    this.messageHandler = this.handleMessage.bind(this);
    // FIX: Use double assertion for type compatibility
    this.channel.addEventListener('message', this.messageHandler as unknown as EventListener);

    // Start leader election process
    this.electLeader();

    // Set up page visibility handler (store reference for cleanup)
    const visibilityHandler = this.handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', visibilityHandler);
    this.eventHandlers.push({ event: 'visibilitychange', handler: visibilityHandler });

    // Set up beforeunload handler (store reference for cleanup)
    const beforeUnloadHandler = this.handleBeforeUnload.bind(this);
    // FIX: Use double assertion for type compatibility
    window.addEventListener('beforeunload', beforeUnloadHandler as unknown as EventListener);
    this.eventHandlers.push({ event: 'beforeunload', handler: beforeUnloadHandler as unknown as EventListener });

    devLog(`[CrossTabSync] Initialized with tabId: ${this.tabId}`);
  }

  public static getInstance(): CrossTabSync {
    if (!CrossTabSync.instance) {
      CrossTabSync.instance = new CrossTabSync();
    }
    return CrossTabSync.instance;
  }

  /**
   * Handle messages from other tabs
   * SECURITY FIX: Added message validation before processing
   */
  private handleMessage(event: MessageEvent<unknown>) {
    // SECURITY FIX: Validate message structure before processing
    if (!isValidTabMessage(event.data)) {
      devWarn('[CrossTabSync] Received invalid message, ignoring');
      return;
    }

    const message = event.data;

    // Ignore our own messages (check both tabId and sourceTabId for echo prevention)
    if (message.tabId === this.tabId || message.sourceTabId === this.tabId) {
      return;
    }

    switch (message.type) {
      case 'leader-election':
        this.handleLeaderElection();
        break;

      case 'leader-heartbeat':
        this.handleLeaderHeartbeat(message);
        break;

      case 'socket-event':
        // Process socket events from other tabs regardless of leader status
        // This supports both WebSocket forwarding (leader->followers) and
        // direct mutation broadcasts (any tab->all other tabs)
        if (message.data) {
          this.handleSocketEvent(message.data);
        }
        break;

      case 'cache-invalidation':
        // Handle cache invalidation from leader
        if (message.data) {
          this.handleCacheInvalidation(message.data);
        }
        break;

      case 'state-sync':
        // Handle state synchronization
        if (message.data) {
          this.handleStateSync(message.data);
        }
        break;

      case 'socket-room-change':
        // Handle room subscription changes from leader tab
        if (!this.isLeader && message.data) {
          this.handleRoomChange(message.data);
        }
        break;
    }
  }

  /**
   * Leader election process
   */
  private electLeader() {
    devLog(`[CrossTabSync] Starting leader election for tab: ${this.tabId}`);

    // Announce our candidacy
    this.broadcast('leader-election', {
      tabId: this.tabId,
      timestamp: Date.now()
    });

    // Wait for responses from other tabs
    setTimeout(() => {
      // If no leader heartbeat received, become leader
      if (Date.now() - this.lastLeaderHeartbeat > this.LEADER_TIMEOUT) {
        this.becomeLeader();
      } else {
        this.becomeFollower();
      }
    }, 1000);

    // Start checking for leader health
    this.startLeaderCheck();
  }

  /**
   * Handle leader election messages
   */
  private handleLeaderElection() {
    // If we're the leader, send heartbeat to establish dominance
    if (this.isLeader) {
      this.sendLeaderHeartbeat();
    }
  }

  /**
   * Handle leader heartbeat messages
   */
  private handleLeaderHeartbeat(message: TabMessage) {
    this.lastLeaderHeartbeat = Date.now();
    this.leaderId = message.tabId;

    // If we thought we were leader but another leader exists, step down
    if (this.isLeader && message.tabId !== this.tabId) {
      // Compare tab IDs to resolve conflict (lowest ID wins)
      if (message.tabId < this.tabId) {
        devLog(`[CrossTabSync] Stepping down as leader, ${message.tabId} has priority`);
        this.becomeFollower();
      }
    }
  }

  /**
   * Become the leader tab
   */
  private becomeLeader() {
    if (this.isLeader) return;

    devLog(`[CrossTabSync] Tab ${this.tabId} becoming leader`);
    this.isLeader = true;
    this.leaderId = this.tabId;

    // Start WebSocket connection (only if not already connected)
    this.socketService = SocketService.getInstance();
    if (!this.socketService.connected) {
      this.socketService.connect();
    }

    // Set up socket event forwarding
    this.setupSocketEventForwarding();

    // Start sending heartbeats
    this.startLeaderHeartbeat();

    // Notify other tabs
    this.sendLeaderHeartbeat();
  }

  /**
   * Become a follower tab
   */
  private becomeFollower() {
    if (!this.isLeader) return;

    devLog(`[CrossTabSync] Tab ${this.tabId} becoming follower`);
    this.isLeader = false;

    // Stop WebSocket connection if we have one
    if (this.socketService) {
      this.socketService.disconnect();
      this.socketService = null;
    }

    // Stop sending heartbeats
    this.stopLeaderHeartbeat();
  }

  /**
   * Start sending leader heartbeats
   */
  private startLeaderHeartbeat() {
    this.stopLeaderHeartbeat();

    this.leaderHeartbeatInterval = setInterval(() => {
      this.sendLeaderHeartbeat();
    }, this.HEARTBEAT_INTERVAL);
  }

  /**
   * Stop sending leader heartbeats
   */
  private stopLeaderHeartbeat() {
    if (this.leaderHeartbeatInterval) {
      clearInterval(this.leaderHeartbeatInterval);
      this.leaderHeartbeatInterval = null;
    }
  }

  /**
   * Send leader heartbeat
   */
  private sendLeaderHeartbeat() {
    this.broadcast('leader-heartbeat', {
      tabId: this.tabId,
      timestamp: Date.now()
    });
  }

  /**
   * Start checking for leader health
   */
  private startLeaderCheck() {
    this.leaderCheckInterval = setInterval(() => {
      // If we're not leader and haven't heard from leader, start election
      if (!this.isLeader && Date.now() - this.lastLeaderHeartbeat > this.LEADER_TIMEOUT) {
        devLog(`[CrossTabSync] Leader timeout, starting new election`);
        this.electLeader();
      }
    }, this.LEADER_TIMEOUT);
  }

  /**
   * Set up forwarding of socket events to other tabs
   * Note: Room subscription changes are now handled by explicit broadcastToTabs calls
   * in socketService methods instead of monkey-patching emit
   */
  private setupSocketEventForwarding() {
    // No longer using monkey-patch - room changes are explicitly forwarded
    // by socketService.broadcastToTabs() in subscription methods
    // This method is kept for backward compatibility but no longer patches emit
  }

  /**
   * Handle socket events from leader tab
   */
  private handleSocketEvent(data: BroadcastData) {
    // Trigger local event handlers
    window.dispatchEvent(new CustomEvent('socket-event', {
      detail: data
    }));
  }

  /**
   * Handle room subscription changes from leader tab
   * This replaces the need for monkey-patching socketService.emit
   */
  private handleRoomChange(data: BroadcastData) {
    // Dispatch a local event that UI components can listen to
    // This ensures follower tabs update their UI when rooms change
    window.dispatchEvent(new CustomEvent('socket-room-change', {
      detail: data
    }));

    // Log for debugging
    devLog(`[CrossTabSync] Follower tab received room change:`, data);
  }

  /**
   * Handle cache invalidation from leader
   */
  private handleCacheInvalidation(data: BroadcastData) {
    // Trigger cache invalidation in React Query
    window.dispatchEvent(new CustomEvent('cache-invalidation', {
      detail: data
    }));
  }

  /**
   * Handle state synchronization
   */
  private handleStateSync(data: BroadcastData) {
    // Trigger state sync event
    window.dispatchEvent(new CustomEvent('state-sync', {
      detail: data
    }));
  }

  /**
   * Handle page visibility changes
   */
  private handleVisibilityChange() {
    if (document.hidden) {
      // Tab going to background
      if (this.isLeader) {
        devLog(`[CrossTabSync] Leader tab going to background`);
        // Optionally transfer leadership
      }
    } else {
      // Tab coming to foreground
      if (!this.isLeader && Date.now() - this.lastLeaderHeartbeat > this.LEADER_TIMEOUT) {
        // If no active leader, start election
        this.electLeader();
      }
    }
  }

  /**
   * Handle tab closing
   */
  private handleBeforeUnload() {
    if (this.isLeader) {
      // Notify other tabs that leader is leaving
      this.broadcast('leader-election', {
        tabId: 'leader-leaving',
        timestamp: Date.now()
      });
    }

    // Clean up
    this.cleanup();
  }

  /**
   * Broadcast message to all tabs
   */
  public broadcast(type: TabMessage['type'], data?: BroadcastData, sourceTabId?: string) {
    if (!this.channel) {
      devWarn('[CrossTabSync] Cannot broadcast - channel is closed');
      return;
    }

    const message: TabMessage = {
      type,
      tabId: this.tabId,
      sourceTabId: sourceTabId || this.tabId,
      timestamp: Date.now(),
      data
    };

    try {
      this.channel.postMessage(message);
    } catch (error: unknown) {
      devWarn('[CrossTabSync] Failed to broadcast message:', error);
    }
  }

  /**
   * Clean up resources
   */
  private cleanup() {
    devLog('[CrossTabSync] Cleaning up resources');

    this.stopLeaderHeartbeat();

    if (this.leaderCheckInterval) {
      clearInterval(this.leaderCheckInterval);
      this.leaderCheckInterval = null;
    }

    // Clean up event listeners
    this.eventHandlers.forEach(({ event, handler }) => {
      if (event === 'visibilitychange') {
        document.removeEventListener(event, handler);
      } else {
        window.removeEventListener(event, handler);
      }
    });
    this.eventHandlers = [];

    // Clean up message handler
    if (this.channel && this.messageHandler) {
      // FIX: Use double assertion for type compatibility
      this.channel.removeEventListener('message', this.messageHandler as unknown as EventListener);
      this.messageHandler = null;
    }

    if (this.socketService) {
      this.socketService.disconnect();
      this.socketService = null;
    }

    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
  }

  /**
   * Public methods
   */

  public getIsLeader(): boolean {
    return this.isLeader;
  }

  public getTabId(): string {
    return this.tabId;
  }

  public getLeaderId(): string | null {
    return this.leaderId;
  }

  /**
   * Send data to all tabs
   */
  public sendToAllTabs(event: string, data: BroadcastData) {
    if (this.channel) {
      this.broadcast('socket-event', { event, data });
    }
  }

  /**
   * Invalidate cache across all tabs
   * Now broadcasts canonical type format { type: 'projects:list' }
   */
  public invalidateCacheAcrossTabs(queryKey: unknown[]) {
    if (this.channel) {
      // Broadcast canonical type format based on query key pattern
      const canonicalType = this.mapQueryKeyToCanonicalType(queryKey);
      if (canonicalType) {
        this.broadcast('cache-invalidation', { type: canonicalType });
      } else {
        // Fallback to legacy queryKey format for unknown patterns
        // @deprecated - Legacy support for queryKey payload format
        this.broadcast('cache-invalidation', { queryKey });
      }
    }
  }

  /**
   * Map query keys to canonical type format
   * @param queryKey Array representing the query key
   * @returns Canonical type string or null if no mapping exists
   */
  private mapQueryKeyToCanonicalType(queryKey: unknown[]): string | null {
    if (!Array.isArray(queryKey) || queryKey.length === 0) {
      return null;
    }

    const firstKey = queryKey[0];

    // Map common query patterns to canonical types
    switch (firstKey) {
      case 'projects':
        return 'projects:list';
      case 'project':
        return queryKey[1] ? `project:${queryKey[1]}` : 'project:update';
      case 'user':
        return queryKey[1] ? `user:${queryKey[1]}` : 'user:update';
      case 'organization':
        return queryKey[1] ? `organization:${queryKey[1]}` : 'organization:update';
      case 'dataroom':
        return queryKey[1] ? `dataroom:${queryKey[1]}` : 'dataroom:update';
      case 'project-notes':
        return queryKey[1] ? `project-notes:${queryKey[1]}` : 'project-notes:update';
      default:
        return null;
    }
  }

  /**
   * @deprecated Use invalidateCacheAcrossTabs instead
   * Backward compatibility wrapper for legacy callers
   */
  public broadcastInvalidation(queryKey: unknown[]) {
    devWarn('CrossTabSync: broadcastInvalidation is deprecated. Use invalidateCacheAcrossTabs instead.');
    this.invalidateCacheAcrossTabs(queryKey);
  }

  /**
   * Sync state across all tabs
   */
  public syncStateAcrossTabs(state: BroadcastData) {
    if (this.channel) {
      this.broadcast('state-sync', state);
    }
  }

  /**
   * REALTIME EVENT CONTRACT - Standardized Events
   * Emit standardized events with colon notation (project:updated, etc.)
   * Also emit legacy hyphen events for backward compatibility (marked deprecated)
   */
  public emitProjectEvent(eventType: 'updated' | 'approved' | 'rejected' | 'pending' | 'aiSynthesisStatusChanged', data: BroadcastData) {
    // Emit standardized colon event
    const standardEvent = `project:${eventType}`;
    this.sendToAllTabs(standardEvent, data);

    // Emit legacy hyphen event with deprecation warning
    // @deprecated - Legacy support for one release cycle
    const legacyEvent = `project-${eventType}`;
    this.sendToAllTabs(legacyEvent, data);

    devLog(`[CrossTabSync] Emitted standardized event: ${standardEvent} with legacy fallback: ${legacyEvent}`);
  }

  /**
   * Register listeners for both standardized and legacy events
   * This method helps components transition to new event format
   */
  public onProjectEvent(eventType: 'updated' | 'approved' | 'rejected' | 'pending' | 'aiSynthesisStatusChanged', handler: (data: BroadcastData) => void) {
    // Listen for standardized colon event
    const standardEvent = `project:${eventType}`;
    window.addEventListener('socket-event', ((event: CustomEvent) => {
      if (event.detail.event === standardEvent) {
        handler(event.detail.data);
      }
    }) as EventListener);

    // Listen for legacy hyphen event (deprecated)
    // @deprecated - Legacy support for one release cycle
    const legacyEvent = `project-${eventType}`;
    window.addEventListener('socket-event', ((event: CustomEvent) => {
      if (event.detail.event === legacyEvent) {
        devWarn(`[CrossTabSync] DEPRECATED: Using legacy event ${legacyEvent}. Please migrate to ${standardEvent}`);
        handler(event.detail.data);
      }
    }) as EventListener);

    return () => {
      // Return cleanup function that removes both listeners
      // FIX: Use double assertion for type compatibility
      window.removeEventListener('socket-event', handler as unknown as EventListener);
    };
  }
}

export default CrossTabSync;
